import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { Button, Dialog, Notice, ViewsNav, ReadOnlyNote } from "@omnabase/ui";
import { formPublishProblem, FORM_LIMITS, FORM_TEXT_DEFAULTS, type FormDefinition } from "@omnabase/shared";
import { Shell } from "../../shell/Shell";
import { useSession } from "../../shell/store";
import { useCan } from "../../lib/access";
import { useFormDefinition } from "../../lib/forms";
import { useLeaveGuard } from "../../lib/leaveGuard";
import { useNav, useWs } from "../../lib/nav";
import { useSettingsGroups } from "../../lib/settingsNav";
import { useTags } from "../../lib/inbox";
import { useScrollReset } from "../../lib/scrollReset";
import { canConnect, keyBefore, type DraftField, type OpenProject } from "../../lib/formEdits";
import { hasProblems, ruleFields } from "../../lib/formDraftRules";
import { EMBED_SUMMARY, shareSummary, tagsSummary, textsSummary, whereSummary } from "../../lib/formSummaries";
import { NoSuchPage } from "./NoSuchPage";
import { useFormBuilder, type FormBuilderState } from "./forms/useFormBuilder";
import { BuilderHead } from "./forms/BuilderHead";
import { AddFieldMenu } from "./forms/AddFieldMenu";
import { FieldList } from "./forms/FieldList";
import { FieldSettings } from "./forms/FieldSettings";
import { FormRows, type FormRowDef, type FormRowId } from "./forms/FormRows";
import { FormTagsSection, type TagChoices } from "./forms/FormTagsSection";
import { FormTextsSection } from "./forms/FormTextsSection";
import { FormShareSection, FormWhereSection } from "./forms/FormWhereShare";
import { EmbedSection } from "./forms/EmbedSection";
import { PreviewPane, type PreviewMode } from "./forms/PreviewPane";
import { usePanelEdits, useTagEdits } from "./forms/formBuilderEdits";

/** The form builder (`/settings/forms/<id>`), builder v2 after the founder's design (brief docs/briefs/forms-builder-v2.md): the settings
 *  column as everywhere, the editing column (the name in place, the fields and Text blocks as rows that open in place, the form's rows under
 *  them) and the large preview with its top bar (Desktop/Mobile, "Unsaved changes", Cancel, Save). Side by side while both columns keep
 *  their minimums (360 and 280 px), each scrolling on its own; stacked below that, the preview after the fields, a "Preview" jump at the top
 *  and Cancel and Save in a bar that stays at the foot of the screen. Saving stays explicit as round one built it: leaving with unsaved
 *  changes asks first, an outdated save is refused and says so. Readers (no project settings) and archived forms see everything read-only. */
export function FormBuilderScreen() {
  const nav = useNav(); const { slug, ws } = useWs(); const { formId = "" } = useParams();
  const groups = useSettingsGroups("forms");
  const form = useFormDefinition(slug, formId);
  const def = form.data?.id === formId ? form.data : null;
  const crumbs = [{ label: "Settings", href: ws("/settings/general") }, { label: "Forms", href: ws("/settings/forms"), views: true }, { label: def?.name ?? "Form" }];
  return (
    <Shell views={<ViewsNav groups={groups} onNavigate={nav} />} crumbs={crumbs} bodyClassName="ob-body--dashboard">
      {def ? <FormBuilder key={def.id} initial={def} /> : <main className="ob-page ob-page--centred">{form.error ? <NoSuchPage /> : null}</main>}
    </Shell>
  );
}

/** both columns side by side from this width of the builder (the editing column's 360 px and the preview's 280 px, and their padding) */
const SPLIT_FROM = 660;
/** whether the builder is wide enough for its two columns, measured on the builder itself (the settings column beside it does not matter) */
function useSplit(): [boolean, (el: HTMLElement | null) => void] {
  const [split, setSplit] = useState(true);
  const [node, setNode] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!node) return;
    const measure = () => setSplit(node.getBoundingClientRect().width >= SPLIT_FROM);
    measure();
    const ro = new ResizeObserver(measure); ro.observe(node);
    return () => ro.disconnect();
  }, [node]);
  return [split, setNode];
}

function FormBuilder({ initial }: { initial: FormDefinition }) {
  const { slug } = useWs(); const { workspace, toast } = useSession();
  const manage = useCan()("projectSettings");
  const projects: OpenProject[] = workspace.projects.filter((p) => !p.archivedAt).map((p) => ({ id: p.id, name: p.name }));
  const b = useFormBuilder(initial, slug, projects, toast);
  const edit = manage && !b.read.archivedAt;
  const [split, measured] = useSplit();
  const [mode, setMode] = useState<PreviewMode>("desktop");
  const [formRow, setFormRow] = useState<FormRowId | null>(null);
  const preview = useRef<HTMLElement | null>(null);
  const blocker = useLeaveGuard(b.dirty);
  const top = useScrollReset(b.read.id);
  const main = useCallback((el: HTMLElement | null) => { top(el); measured(el); }, [top, measured]);
  const full = b.draft.items.length >= FORM_LIMITS.fields;
  const rows = useFormRowDefs(b, edit, projects, workspace.slug);
  const actions = edit ? <SaveActions b={b} /> : null;
  const tools = <>
    {!split && <Button variant="ghost" size="sm" icon="eye" onClick={() => preview.current?.scrollIntoView({ block: "start" })}>Preview</Button>}
    {edit && <AddFieldMenu full={full} onPick={b.add} onClosedAfterPick={b.refocus} />}
  </>;

  return (
    <main className={`ob-page ob-form-builder ${split ? "ob-form-builder--split" : "ob-form-builder--stacked"}`} aria-label="Form builder" ref={main}>
      <div className="ob-form-builder__edit">
        <BuilderHead name={b.draft.name} problem={b.problems.name} edit={edit} onName={b.rename} tools={tools} />
        <div className="ob-form-builder__edit-body">
          {!manage && <ReadOnlyNote object="form" />}{/* the polish round: why nothing here can be changed */}
          {b.read.archivedAt && <p className="ob-form-builder__hint">Archived: restore it on the Forms page to change it.</p>}
          {b.outdated && <Notice icon="warning" action={<Button size="sm" onClick={() => void b.reload()}>Reload</Button>}>Someone changed this form since you opened it. Reload to see their version; your changes here are not saved.</Notice>}
          <FieldList items={b.draft.items} open={b.open} edit={edit} problems={new Set([...b.problems.fields.keys(), ...b.problems.keys.keys()])} reveal={b.reveal}
            onToggle={b.toggle} onMove={b.move} settingsOf={(f) => <OpenField b={b} field={f} edit={edit} full={full} projects={projects} />} />
          <FormRows rows={rows} open={formRow} onToggle={(id) => setFormRow((o) => (o === id ? null : id))} />
        </div>
      </div>
      <PreviewPane items={b.draft.items} submit={b.draft.texts.submit.trim() || FORM_TEXT_DEFAULTS.submit} mode={mode} onMode={setMode} actions={split ? actions : null} open={b.open} onOpenField={b.showField}
        paneRef={(el) => { preview.current = el; }} />
      {!split && actions && <div className="ob-form-builder__foot">{actions}</div>}
      {blocker.state === "blocked" && (
        <Dialog title="Unsaved changes" intro="Your changes to this form are not saved yet. Leave without saving, and they are gone." onClose={() => blocker.reset()}
          footer={<><span className="ob-dialog__foot-meta" /><Button onClick={() => blocker.reset()}>Stay</Button><Button variant="primary" onClick={() => blocker.proceed()}>Leave without saving</Button></>} />
      )}
    </main>
  );
}

/** "Unsaved changes", Cancel and Save: in the preview's top bar on a wide screen, in the bar at the foot on a narrow one */
function SaveActions({ b }: { b: FormBuilderState }) {
  return (
    <div className="ob-form-builder__actions">
      {b.dirty && <span className="ob-form-builder__unsaved">Unsaved changes</span>}
      <Button disabled={!b.dirty || b.busy} onClick={b.cancel}>Cancel</Button>
      <Button variant="primary" busy={b.busy} disabled={!b.dirty || b.busy || hasProblems(b.problems)} onClick={() => void b.save()}>Save</Button>
    </div>
  );
}

/** the open field's settings with its checks and its Duplicate and Delete */
function OpenField({ b, field: f, edit, full, projects }: { b: FormBuilderState; field: DraftField; edit: boolean; full: boolean; projects: readonly OpenProject[] }) {
  const edits = usePanelEdits(b.setDraft, f.uid, projects);
  const checks = { keyProblem: b.problems.keys.get(f.uid) ?? null, keyWas: keyBefore(b.saved, f), fieldProblem: b.problems.fields.get(f.uid) ?? null };
  return <FieldSettings field={f} edit={edit} checks={checks} projects={projects} edits={edits} full={full} focusLabel={b.focus?.uid === f.uid ? b.focus.n : null} canConnect={(m) => canConnect(b.draft, f, m)} onDuplicate={() => b.duplicate(f.uid)} onDelete={() => b.remove(f.uid)} />;
}

/** the form's five rows: their titles, marks, summaries (following the draft) and bodies */
function useFormRowDefs(b: FormBuilderState, edit: boolean, projects: readonly OpenProject[], workspaceSlug: string): FormRowDef[] {
  const { toast, workspace } = useSession(); const { slug } = useWs();
  const tags = useTags(slug).data ?? [];
  const tagEdits = useTagEdits(b.setDraft);
  const d = b.draft;
  const choices: TagChoices = { ticket: tags.filter((t) => t.kind !== "customer"), customer: tags.filter((t) => t.kind === "customer"), // the form editor round: a project's own ticket tags too
    projects: new Map(workspace.projects.map((p) => [p.id, p.archivedAt ? `${p.name} (archived)` : p.name])), unseen: b.read.unseenTagIds ?? [] };
  const copy = async (text: string, done: string) => { try { await navigator.clipboard.writeText(text); toast(done, { kind: "success" }); } catch { toast("Could not copy", { kind: "error" }); } };
  return [
    { id: "tags", title: "Tags", icon: "sell", summary: tagsSummary(d.tags), body: <FormTagsSection tags={d.tags} rules={d.tags.rules} selects={ruleFields(d)} choices={choices} edit={edit} problems={b.problems.rules} edits={tagEdits} /> },
    { id: "texts", title: "Texts", icon: "text", summary: textsSummary(d.texts), body: <FormTextsSection texts={d.texts} edit={edit} onText={(k, v) => b.setDraft((x) => ({ ...x, texts: { ...x.texts, [k]: v } }))} /> },
    { id: "where", title: "Where submissions go", icon: "moveToInbox", summary: whereSummary(d, projects), body:
      <FormWhereSection defaultProjectId={d.defaultProjectId} published={d.published} projects={projects} problem={formPublishProblem({ fields: { items: d.items }, defaultProjectId: d.defaultProjectId })} edit={edit}
        onDefault={(id) => b.setDraft((x) => ({ ...x, defaultProjectId: id }))} onPublished={(on) => b.setDraft((x) => ({ ...x, published: on }))} /> },
    { id: "embed", title: "Embed in your page", icon: "code", summary: EMBED_SUMMARY, body: <EmbedSection workspaceSlug={workspaceSlug} formId={b.read.id} onCopy={(text, done) => void copy(text, done)} /> },
    { id: "share", title: "Share", icon: "link", summary: shareSummary(d.slug), body: <FormShareSection workspaceSlug={workspaceSlug} slug={d.slug} problem={b.problems.slug} edit={edit} onSlug={(s) => b.setDraft((x) => ({ ...x, slug: s }))} onCopy={(a) => void copy(a, "Address copied")} /> },
  ];
}
